/**
 * These estimates are based on the average of tx simulations from personal testing.
 * We hardcode these fee values because we can't simulate balance changes unless we have the actual txns.
 * Meaning we would have to generate all the txns on page load, since we currently generate the txn on confirm button.
 * **/

import { BASE_FEE_LAMPORTS } from "@bridgesplit/utils";
import { TransactionFees } from "app/components/transactions";
import { TransactionTypes } from "app/types";

export const NETWORK_FEE_BUFFER = 459500;

// Borrow
export const AVG_BORROW_REFUNDED_RENT = 22540000;
export const AVG_BORROW_RENT = 10002000;
export const TOTAL_BORROW_FEE = AVG_BORROW_REFUNDED_RENT + AVG_BORROW_RENT + BASE_FEE_LAMPORTS;

// Lend - Advanced custom strategies
const STRATEGY_ACCOUNT_RENT_REFUNDABLE = 59755355; // Parent strategy account
const STRATEGY_LOOPUP_TABLE_RENT_NON_REFUNDABLE = 1948000; // Lookup table (never refunded)
const SOLANA_TOKEN_ACCOUNT_RENT_REFUNDABLE = 2039000; // Principal storage account
const EXTERNAL_YIELD_RENT_REFUNDABLE = 16980000; // External yield account

// No external yield lend scenario
const NO_EXTERNAL_YIELD_LEND_TOTAL_RENT =
    STRATEGY_ACCOUNT_RENT_REFUNDABLE + STRATEGY_LOOPUP_TABLE_RENT_NON_REFUNDABLE + SOLANA_TOKEN_ACCOUNT_RENT_REFUNDABLE;
const NO_EXTERNAL_YIELD_LEND_REFUNDABLE_RENT = STRATEGY_ACCOUNT_RENT_REFUNDABLE + SOLANA_TOKEN_ACCOUNT_RENT_REFUNDABLE;

// External yield lend scenario
const EXTERNAL_YIELD_LEND_TOTAL_RENT =
    STRATEGY_ACCOUNT_RENT_REFUNDABLE + STRATEGY_LOOPUP_TABLE_RENT_NON_REFUNDABLE + EXTERNAL_YIELD_RENT_REFUNDABLE;

const EXTERNAL_YIELD_LEND_REFUNDABLE_RENT_EXCLUDING_EXTERNAL_YIELD = STRATEGY_ACCOUNT_RENT_REFUNDABLE; // For UI breakdown

// Total fees including network fees
const NO_EXTERNAL_YIELD_TOTAL_LEND_FEE = NO_EXTERNAL_YIELD_LEND_TOTAL_RENT + BASE_FEE_LAMPORTS;
const EXTERNAL_YIELD_TOTAL_LEND_FEE = EXTERNAL_YIELD_LEND_TOTAL_RENT + BASE_FEE_LAMPORTS;

//Refinance
export const AVG_REFINANCE_REFUNDED_RENT = 0;
export const AVG_REFINANCE_RENT = 0;
export const TOTAL_REFINANCE_FEE = AVG_REFINANCE_REFUNDED_RENT + AVG_REFINANCE_RENT + BASE_FEE_LAMPORTS;

//Borrow More
export const AVG_BORROW_MORE_REFUNDED_RENT = 2040000;
export const AVG_BORROW_MORE_RENT = 27650000;
export const TOTAL_BORROW_MORE_FEE = AVG_BORROW_MORE_REFUNDED_RENT + AVG_BORROW_MORE_RENT + BASE_FEE_LAMPORTS;

// Repay
export const AVG_REPAY_REFUNDED_RENT = 0;
export const AVG_REPAY_RENT = 6240000;
export const TOTAL_REPAY_FEE = AVG_REPAY_REFUNDED_RENT + AVG_REPAY_RENT + BASE_FEE_LAMPORTS;

// Loop Borrow
export const AVG_EARN_FIRST_SETUP_ACCOUNT = 10000000 + BASE_FEE_LAMPORTS;
export const AVG_EARN_REFUNDED_RENT = 20200000; //You get more then the rent, this its likley closing an ATA account
export const AVG_EARN_RENT = 7800000;
export const TOTAL_EARN_FEE = AVG_EARN_RENT + AVG_EARN_REFUNDED_RENT + BASE_FEE_LAMPORTS;

// Loop Unwind
export const AVG_LOOP_UNWIND_RENT = 12000000;
export const TOTAL_LOOP_UNWIND_FEE = AVG_LOOP_UNWIND_RENT + BASE_FEE_LAMPORTS;

// Vault Deposit
export const AVG_VAULT_DEPOSIT_RENT = 10000000;
export const TOTAL_VAULT_DEPOSIT_FEE = AVG_VAULT_DEPOSIT_RENT + BASE_FEE_LAMPORTS;

// Vault Withdraw
export const AVG_VAULT_WITHDRAW_RENT = 10000000;
export const TOTAL_VAULT_WITHDRAW_FEE = AVG_VAULT_WITHDRAW_RENT + BASE_FEE_LAMPORTS;

/**
 * The following events have no rent or refunded rent. Just the network fee, a priority fee and a buffer from the wallet.
 * - Topup
 * - Loan Withdraw
 * - Strategy Deposit
 * - Strategy Withdraw
 * **/

export const DEFAULT_TX_FEE = BASE_FEE_LAMPORTS + NETWORK_FEE_BUFFER;
export const ZERO_FEE = 0;

export type TransactionData = {
    [key in TransactionTypes]: TransactionFees;
};

// Currently no support for setupFees
export const TRANSACTION_FEE_ESTIMATES: TransactionData = {
    [TransactionTypes.MarketBorrow]: {
        rentAmount: AVG_BORROW_RENT,
        totalFeeAmount: TOTAL_BORROW_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: AVG_BORROW_REFUNDED_RENT,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.MarketLend]: {
        rentAmount: NO_EXTERNAL_YIELD_LEND_TOTAL_RENT,
        totalFeeAmount: NO_EXTERNAL_YIELD_TOTAL_LEND_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: NO_EXTERNAL_YIELD_LEND_REFUNDABLE_RENT,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.MarketLendExternalYield]: {
        rentAmount: EXTERNAL_YIELD_LEND_TOTAL_RENT,
        totalFeeAmount: EXTERNAL_YIELD_TOTAL_LEND_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: EXTERNAL_YIELD_LEND_REFUNDABLE_RENT_EXCLUDING_EXTERNAL_YIELD,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: EXTERNAL_YIELD_RENT_REFUNDABLE
    },
    [TransactionTypes.Refinance]: {
        rentAmount: AVG_REFINANCE_RENT,
        totalFeeAmount: TOTAL_REFINANCE_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: AVG_REFINANCE_REFUNDED_RENT,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.BorrowMore]: {
        rentAmount: AVG_BORROW_MORE_RENT,
        totalFeeAmount: TOTAL_BORROW_MORE_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: AVG_BORROW_MORE_REFUNDED_RENT,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.Repay]: {
        rentAmount: AVG_REPAY_RENT,
        totalFeeAmount: TOTAL_REPAY_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: AVG_REPAY_REFUNDED_RENT,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.Topup]: {
        rentAmount: ZERO_FEE,
        totalFeeAmount: DEFAULT_TX_FEE,
        networkAmount: DEFAULT_TX_FEE,
        refundAmount: ZERO_FEE,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.LoanWithdraw]: {
        rentAmount: ZERO_FEE,
        totalFeeAmount: DEFAULT_TX_FEE,
        networkAmount: DEFAULT_TX_FEE,
        refundAmount: ZERO_FEE,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.StrategyDeposit]: {
        rentAmount: ZERO_FEE,
        totalFeeAmount: DEFAULT_TX_FEE,
        networkAmount: DEFAULT_TX_FEE,
        refundAmount: ZERO_FEE,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.StrategyWithdraw]: {
        rentAmount: ZERO_FEE,
        totalFeeAmount: DEFAULT_TX_FEE,
        networkAmount: DEFAULT_TX_FEE,
        refundAmount: ZERO_FEE,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.EarnBorrow]: {
        rentAmount: AVG_EARN_RENT,
        totalFeeAmount: TOTAL_EARN_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: AVG_EARN_REFUNDED_RENT,
        setupAmount: AVG_EARN_FIRST_SETUP_ACCOUNT,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.Generic]: {
        rentAmount: AVG_EARN_RENT,
        totalFeeAmount: TOTAL_EARN_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: AVG_EARN_REFUNDED_RENT,
        setupAmount: AVG_EARN_FIRST_SETUP_ACCOUNT,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.LoopUnwind]: {
        rentAmount: AVG_LOOP_UNWIND_RENT,
        totalFeeAmount: TOTAL_LOOP_UNWIND_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: ZERO_FEE,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.VaultDeposit]: {
        rentAmount: AVG_VAULT_DEPOSIT_RENT,
        totalFeeAmount: TOTAL_VAULT_DEPOSIT_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: ZERO_FEE,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.VaultWithdraw]: {
        rentAmount: AVG_VAULT_WITHDRAW_RENT,
        totalFeeAmount: TOTAL_VAULT_WITHDRAW_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: ZERO_FEE,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.EarnDeposit]: {
        rentAmount: AVG_VAULT_DEPOSIT_RENT,
        totalFeeAmount: TOTAL_VAULT_DEPOSIT_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: ZERO_FEE,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    },
    [TransactionTypes.EarnWithdraw]: {
        rentAmount: AVG_VAULT_WITHDRAW_RENT,
        totalFeeAmount: TOTAL_VAULT_WITHDRAW_FEE,
        networkAmount: BASE_FEE_LAMPORTS,
        refundAmount: ZERO_FEE,
        setupAmount: ZERO_FEE,
        externalYieldRentAmount: ZERO_FEE
    }
};
